import React, { useState, useCallback, useEffect, useRef } from 'react';
import { rootApi } from '../api/api';
import styles from '../styles/LocationSearch.module.css';

const LocationSearch = ({ onSelect, placeholder = 'Search for place, district, or state...' }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchTime, setSearchTime] = useState(0);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  const searchLocations = useCallback(async (searchTerm) => {
    // Trim before the length check so it matches the backend's trimmed validation.
    // Without this, a trailing space ("ab ") passes here but fails server-side with a 400.
    const trimmedTerm = (searchTerm || '').trim();
    if (trimmedTerm.length < 3) {
      setResults([]);
      setIsOpen(false);
      setSearchTime(0);
      return;
    }

    // Cancel previous request if still running
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const { data } = await rootApi.get('/search', {
        params: {
          q: trimmedTerm,
          limit: 20,
        },
        signal: abortControllerRef.current.signal
      });
      setResults(data.data || []);
      setSearchTime(data.searchTime || 0);
      setIsOpen(true);
      setSelectedIndex(-1);
    } catch (error) {
      if (error.name === 'CanceledError') {
        // Request was cancelled, do nothing
        return;
      }
      console.error('Location search error:', error);
      setResults([]);
      setSearchTime(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchLocations(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, searchLocations]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Cleanup abort controller on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleSelect = (location) => {
    setQuery(`${location.name}, ${location.district_name}, ${location.state_name}, ${location.country}`);
    setIsOpen(false);
    if (onSelect) {
      onSelect({
        ...location,
        searchTime
      });
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleSelect(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapperRef} className={styles.searchWrapper}>
      <div className={styles.inputWrapper}>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={styles.searchInput}
          onFocus={() => {
            query.length >= 3 && results.length > 0 && setIsOpen(true);
          }}
        />
        {loading && (
          <div className={styles.loadingSpinner}>
            <div className={styles.spinner} />
          </div>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <div className={styles.dropdown}>
          {results.map((location, index) => (
            <div
              key={location.id}
              onClick={() => handleSelect(location)}
              className={`${styles.resultItem} ${selectedIndex === index ? styles.selected : ''}`}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className={styles.resultName}>
                {location.name}
              </div>
              <div className={styles.resultDetails}>
                {location.district_name} • {location.state_name} • {location.country}
              </div>
            </div>
          ))}
        </div>
      )}

      {isOpen && query.length >= 3 && results.length === 0 && !loading && (
        <div className={styles.noResults}>
          No locations found
        </div>
      )}
    </div>
  );
};

export default LocationSearch;
